import React, { useState, useEffect, useCallback } from 'react';
import { Clock, LogIn, LogOut, Coffee, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const SELF_CLOCK_ROLES = ['sales_manager', 'marketing_assistant', 'marketing_coordinator', 'tech_support', 'operations_manager'];

export default function SelfClockWidget({ userRole }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const canSelfClock = SELF_CLOCK_ROLES.includes(userRole);

  const fetchStatus = useCallback(async () => {
    try {
      const d = await apiRequest('/kiosk/self-clock/status');
      setStatus(d);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canSelfClock) fetchStatus(); else setLoading(false); }, [canSelfClock, fetchStatus]);

  const doAction = async (action) => {
    setActing(true);
    try {
      const res = await apiRequest(`/kiosk/self-clock/${action}`, { method: 'POST' });
      toast.success(res.message);
      fetchStatus();
    } catch (e) { toast.error(e.message || 'Failed'); } finally { setActing(false); }
  };

  if (!canSelfClock || loading) return null;

  const { is_clocked_in, is_on_break, hours_today } = status || {};

  return (
    <Card className="border-l-4 border-l-blue-500" data-testid="self-clock-widget">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${is_clocked_in ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-slate-900">Time Clock</span>
                {is_clocked_in ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{is_on_break ? 'On Break' : 'Clocked In'}</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 text-[10px]">Not Clocked In</Badge>
                )}
              </div>
              {hours_today && <p className="text-xs text-slate-500">Today: {hours_today}</p>}
            </div>
          </div>

          <div className="flex gap-2">
            {!is_clocked_in ? (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" onClick={() => doAction('clock_in')} disabled={acting} data-testid="clock-in-btn">
                <LogIn className="h-4 w-4 mr-1" /> Clock In
              </Button>
            ) : (
              <>
                {!is_on_break ? (
                  <>
                    <Button size="sm" variant="outline" className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => doAction('break_start')} disabled={acting} data-testid="break-start-btn">
                      <Coffee className="h-4 w-4 mr-1" /> Break
                    </Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 h-9" onClick={() => doAction('clock_out')} disabled={acting} data-testid="clock-out-btn">
                      <LogOut className="h-4 w-4 mr-1" /> Clock Out
                    </Button>
                  </>
                ) : (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" onClick={() => doAction('break_end')} disabled={acting} data-testid="break-end-btn">
                    <Play className="h-4 w-4 mr-1" /> End Break
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
